/**
 * The seated leg, as numbers: the outline, its mirror, and the band on it.
 *
 * Pulled out of the component because it stopped being a drawing. The band she
 * injects into used to be derived from her own landmarks, the way the abdomen's
 * is, and Anaïs's call on 2026-09-24 was to anchor it to this outline instead —
 * *"prendre pour repère la silhouette de base, parce que la patiente sera
 * placée en fonction"*. She is right, and for a reason the abdomen does not
 * share: there is no silhouette to stand inside for an abdomen, so the outline
 * there has to find her. Here she is being asked to put her leg in a specific
 * place, and once she has, the band's position is known — it is a property of
 * the drawing, not a measurement of her.
 *
 * The band drawn here was later removed from the screen entirely — see
 * `LEG_BAND` — so what survives of that decision is the numbers, which
 * `pose/zones.ts` divides its own extents out of. The outline and the mark on
 * her leg are therefore the same band without being the same drawing.
 *
 * Everything below is in the outline's own `120 × 160` viewBox, so it is
 * registered to the drawing by construction rather than by two sets of
 * constants agreeing.
 */

export const LEG_VIEWBOX = { width: 120, height: 160 } as const;

/**
 * A seated leg, drawn as a leg.
 *
 * Two attempts failed before this one, and both failed the same way: they did
 * not say what goes where. First two open curves — the torso's grammar, which
 * works on a body because it flares at the shoulders and draws in at the waist,
 * and beside a limb is two unexplained lines. Then a dashed corridor, which is
 * unambiguous about *an area* and silent about which part of her fills it
 * (Anaïs, 2026-09-24).
 *
 * So the outline is recognisable: the hip at the left, the thigh running
 * **level** across the frame, the knee bent square, the calf dropping straight
 * down. Level and not diagonal because that is what a seated thigh looks like
 * to a phone beside her — the first version sloped it, and Anaïs's own drawing
 * of a seated profile says otherwise (2026-09-24). The leg makes a right angle,
 * and the drawing makes the same one.
 *
 * The calf is drawn rather than boxed: widest just below the knee, narrowing
 * toward the ankle, hollow behind the joint. It was two vertical lines and a
 * cut, which reads as a column and not as a leg.
 *
 * It is one closed path rather than an assembly, so the silhouette cannot
 * acquire a seam, and it is generous everywhere: it has to fit every leg, and a
 * dashed outline that a patient has to match exactly would be a measurement,
 * which this is not. Widened twice at Anaïs's request — a thigh traced at one
 * body's width is an outline most people fall outside of, which is the same
 * failure the drawn abdomen was deleted for.
 *
 * It sits low in the frame and the calf runs off the bottom edge on purpose.
 * A seated leg is not contained by a phone propped at chest height: what has to
 * be in shot is the hip and the knee, and pretending the ankle belongs there
 * would ask her to step back from a picture she is about to inject into.
 *
 * Every command here takes its arguments as x,y pairs, which is what `mirrored`
 * relies on. `A`, `H` and `V` would not, and are deliberately absent.
 */
export const SEATED_LEG = [
  "M 8 66", // the hip, at the left edge and a little below centre
  "Q 50 62 96 68", // along the top of the thigh, level
  "Q 114 72 116 92", // over the knee
  "Q 116 124 109 168", // down the shin, nearly straight and drifting in
  "L 85 168", // cut off there rather than drawn to an ankle
  "Q 74 146 72 122", // back up through the belly of the calf
  "Q 70 106 84 98", // and into the hollow behind the knee
  "Q 82 110 70 114", // under the knee
  "Q 42 120 10 118", // back along the underside of the thigh
  "Q 2 90 8 66", // and up the front of the hip
  "Z",
].join(" ");

/**
 * The same path, reflected through the middle of the viewBox.
 *
 * Computed rather than drawn a second time, so the two sides cannot drift apart
 * under editing — the torso's two curves are the same idea written out twice,
 * which is a standing invitation to fix one of them and not the other.
 *
 * Done in numbers and not with an SVG `transform`: that is a thing
 * `react-native-svg` may hand to React Native's own transform parser depending
 * on platform and version, and when it is not taken the component renders
 * nothing at all. An outline facing the wrong way is a bug someone reports; an
 * outline that is simply absent looks like the feature never worked.
 */
export function mirrored(path: string): string {
  const tokens = path.split(" ");
  const isNumber = (token: string | undefined) =>
    /^-?[\d.]+$/.test(token ?? "");

  return tokens
    .map((token, index) => {
      if (!isNumber(token)) return token;
      // How many numbers back to the command letter this one belongs to. Every
      // command here takes x,y pairs, so the even positions are the x's.
      let position = 0;
      for (
        let back = index - 1;
        back >= 0 && isNumber(tokens[back]);
        back -= 1
      ) {
        position += 1;
      }
      return position % 2 === 0
        ? String(LEG_VIEWBOX.width - Number(token))
        : token;
    })
    .join(" ");
}

/**
 * Where the thigh's own axis runs in the drawing, read off the path above.
 *
 * Named so the band cannot drift from the leg it is drawn on: change the path
 * and these are the four numbers to change with it, in one place, rather than
 * an ellipse somewhere else that still looks fine and is no longer on the leg.
 */
export const LEG_AXIS = {
  hipX: 8,
  kneeX: 116,
  /** The middle of the thigh's depth, where the band is centred. */
  y: 91,
} as const;

const HIP_X = LEG_AXIS.hipX;
const KNEE_X = LEG_AXIS.kneeX;
const AXIS_Y = LEG_AXIS.y;

/**
 * The band, along the leg and inside every boundary the sources name.
 *
 * Not drawn, anywhere. It was, on the aiming screen, as a target — and that
 * screen is about where to put your leg, so a mark saying where the needle
 * goes was one instruction too many at the moment she is still moving a phone
 * around (Anaïs, 2026-09-24). What survives is the numbers: `pose/zones.ts`
 * divides the fraction along the leg and both extents out of them, so the band
 * it measures on her own thigh is this band, at her size, wherever she is.
 *
 * Which also means the thigh guide needs a detector exactly as much as the
 * abdomen's does. An earlier version of this comment claimed otherwise, from
 * the days when the band was drawn; on a phone there is no pose model, and what
 * she gets is the mirror and the words, same as everywhere else.
 *
 * ### Along the leg — the whole middle third
 *
 * The sources disagree and the disagreement is recorded in
 * `docs/features/injection-coach/TECHNIQUE.md`: the ganirelix label says the
 * **upper** thigh, the nursing literature says the **middle third**. This was
 * their intersection — the upper half of the middle third, Anaïs's call on the
 * morning of 2026-09-24 — and it is their *union within the middle third* now,
 * on her instruction the same evening and for a reason that overturns the
 * first one: a small outline that is slightly wrong points a patient at the
 * wrong place, where a large one still contains the right one. *"Afin d'être
 * sûr de ne pas mal indiquer la patiente."*
 *
 * So the band runs 0.33 to 0.67 of the way from hip to knee. That is a named
 * region rather than a bigger number — the one both the IM vastus lateralis
 * guidance and general self-injection guidance point at, comfortably clear of
 * the knee and the groin, with the label's "upper thigh" sitting inside its
 * upper half. A patient told either sentence finds herself inside this.
 *
 * ### Across the leg — the anterior part of what the camera can see
 *
 * This was bounded wrongly and the correction is what made room in it. It was
 * sized as "the outer half of the thigh's width", which is the right rule and
 * the wrong axis: seen from the side, the medio-lateral width is the dimension
 * pointing **at the camera**, and the whole visible face of the leg is already
 * the outer one. What runs across the leg on screen is front-to-back.
 *
 * On that axis the rule is different and comes from §3: stay off the **back**
 * of the thigh, where the sciatic nerve is. So the band covers the anterior
 * part of the visible depth and is pushed toward the front.
 *
 * This does **not** disagree with the step's words, though it reads like it at
 * a glance and two reviewers took it that way. The sentence says the outer half
 * of the thigh; from the side, every visible pixel of the leg *is* the outer
 * half, because the inner half is behind it. The band is therefore inside what
 * the words say, biased forward within it — the antero-lateral thigh, which is
 * the site both the label and the practice literature describe. What was wrong
 * was using the outer-half rule to pick a *size* on an axis it says nothing
 * about — up, in the drawing, because a seated thigh's
 * quadriceps face the ceiling whichever way she is turned, which is what makes
 * "up" safe to hard-code here.
 *
 * Still longer than it is deep, so it reads as an ovoid lying along the leg
 * rather than a ring around it — *"elle semble un peu sur un autre plan"*.
 */
export const LEG_BAND = {
  cx: HIP_X + (KNEE_X - HIP_X) * 0.5,
  // Above the axis: the front of the thigh, not the middle of it.
  cy: AXIS_Y - 7,
  rx: ((KNEE_X - HIP_X) * (0.67 - 0.33)) / 2,
  ry: 15,
} as const;
